import { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';

export const isDevelopment = process.env.NODE_ENV === 'development';

export const validateEmail = (email: string): boolean => {
	const mailformat =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

	return mailformat.test(email);
};

export const validateName = (str: string): boolean => {
	const regex = /^([A-Za-z .'\-`]{2,})$/;

	return regex.test(str);
};

const flattenErrorMessages = (value: unknown): string[] => {
	if (typeof value === 'string') return value.trim() ? [value] : [];
	if (Array.isArray(value)) return value.flatMap(flattenErrorMessages);
	if (!value || typeof value !== 'object') return [];

	return Object.values(value).flatMap(flattenErrorMessages);
};

export const getErrorMessages = (error: unknown): string[] => {
	if (typeof error === 'string') return flattenErrorMessages(error);
	if (!error || typeof error !== 'object') return [];

	const responseError = error as {
		detail?: unknown;
		errors?: unknown;
		message?: unknown;
		name?: unknown;
		title?: unknown;
	};

	if (responseError.name === 'AbortError') return [];

	const validationMessages = flattenErrorMessages(responseError.errors);
	const messages = validationMessages.length
		? validationMessages
		: flattenErrorMessages(responseError.title ?? responseError.detail ?? responseError.message);

	return Array.from(new Set(messages));
};

export const errorNormalizer = (error: unknown) => {
	const messages = getErrorMessages(error);
	if (messages.length) toast.error(messages.join(' '));
};

export const capitalizer = (input: string): string =>
	input.replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));

export const validateNumber = (value: string) => {
	const regex = /^(0|[1-9]\d*)?$/;

	return regex.test(value);
};

export const validateDecimalNumber = (value: string) => {
	const regex = /^(0|[1-9]\d*)?(\.\d{0,2})?$/;

	return regex.test(value);
};

export const dateNormalizer = (date: Dayjs) => date.format('YYYY-MM-DD').split('T')[0];

export const getInitials = (name: string): string =>
	name.split(' ').filter(Boolean).map((w) => w[0].toUpperCase()).join('').slice(0, 2);

export const formatClientName = (fullName: string, isElc = false): string =>
	isElc ? `${fullName} (ELC)` : fullName;

export const shortenClientName = (fullName: string): string => {
	const parts = fullName.trim().split(/\s+/);
	if (parts.length <= 1) return parts[0] || '';
	const firstName = parts[0];
	const lastInitial = parts[parts.length - 1][0];

	return `${firstName} ${lastInitial}.`;
};

export const getClientAge = (dateOfBirthday: string): number | null => {
	if (!dateOfBirthday) return null;
	const dob = new Date(dateOfBirthday);
	const today = new Date();
	let age = today.getFullYear() - dob.getFullYear();

	if (dob > new Date(today.getFullYear() - age, today.getMonth(), today.getDate())) age--;

	return age;
};

export const appendAgeSuffix = (displayName: string, dateOfBirth: string | null): string => {
	const age = dateOfBirth ? getClientAge(dateOfBirth) : null;

	return age == null ? displayName : `${displayName}, Age ${age}`;
};

interface ClientLabelSource {
	fullName: string;
	dateOfBirthday?: string | null;
	isElc?: boolean;
}

// Composes shortenClientName + formatClientName + appendAgeSuffix in the one fixed order every
// dashboard client card needs. Use the individual helpers above directly for any other shape.
export const getClientDisplayLabel = (client: ClientLabelSource, { shorten = false } = {}): string => {
	const name = shorten ? shortenClientName(client.fullName) : client.fullName;

	return appendAgeSuffix(formatClientName(name, client.isElc), client.dateOfBirthday ?? null);
};

// this helper mostly used for backend generated dates
// export const dateLocalizer = (date: string) => {
//     const timeZone = -new Date().getTimezoneOffset() / 60;

//     return dayjs(date)
//         .add(timeZone, "hour")
// }
